/**
 * Shared constants + tiny UI atoms for Capacity Planner (Phase 4+).
 */
import React, { useEffect } from "react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/capacity`;

export const STATUS_META = {
  new_request:          { label: "New request",       dot: "bg-slate-400"   , rowTint: "" },
  site_visit_needed:    { label: "Site visit",        dot: "bg-slate-400"   , rowTint: "" },
  waiting_for_estimate: { label: "Needs estimate",    dot: "bg-orange-500"  , rowTint: "bg-orange-500/5" },
  offer_sent:           { label: "Offer sent",        dot: "bg-sky-500"     , rowTint: "bg-sky-500/5" },
  waiting_for_customer: { label: "Waiting cust.",     dot: "bg-purple-400"  , rowTint: "bg-purple-500/10" },
  accepted:             { label: "Accepted",          dot: "bg-emerald-400" , rowTint: "" },
  ready_to_schedule:    { label: "Ready to plan",     dot: "bg-cyan-500"    , rowTint: "" },
  not_started:          { label: "Not started",       dot: "bg-sky-400"     , rowTint: "bg-sky-500/5" },
  noted:                { label: "Noted",             dot: "bg-sky-300"     , rowTint: "bg-sky-500/5" },
  tentative:            { label: "Tentative",         dot: "bg-amber-500"   , rowTint: "" },
  confirmed:            { label: "Confirmed",         dot: "bg-emerald-500" , rowTint: "" },
  in_daily_plan:        { label: "In daily plan",     dot: "bg-emerald-600" , rowTint: "" },
  in_progress:          { label: "In progress",       dot: "bg-yellow-400"  , rowTint: "bg-yellow-500/10" },
  paused:               { label: "Paused",            dot: "bg-orange-400"  , rowTint: "bg-orange-500/5" },
  completed:            { label: "Completed",         dot: "bg-emerald-500" , rowTint: "bg-emerald-500/10" },
  rejected:             { label: "Rejected",          dot: "bg-red-500"     , rowTint: "bg-slate-800/40 opacity-60" },
  cancelled:            { label: "Cancelled",         dot: "bg-slate-500"   , rowTint: "bg-slate-800/40 opacity-60" },
};

export const STATUS_BUCKET_STYLE = {
  available:   { ring: "#10b981", chip: "bg-emerald-500/15 text-emerald-300", label: "Available" },
  busy:        { ring: "#eab308", chip: "bg-yellow-500/15 text-yellow-300",   label: "Busy" },
  almost_full: { ring: "#f97316", chip: "bg-orange-500/15 text-orange-300",   label: "Almost full" },
  full:        { ring: "#ef4444", chip: "bg-red-500/15 text-red-300",         label: "Full" },
  overbooked:  { ring: "#dc2626", chip: "bg-red-600/25 text-red-200",         label: "Overbooked" },
};

export const TEAM_COLOR = {
  cyan: "bg-cyan-500", emerald: "bg-emerald-500", amber: "bg-amber-500",
  slate: "bg-slate-500", purple: "bg-purple-500", orange: "bg-orange-500",
  blue: "bg-blue-500", pink: "bg-pink-500",
};

export const authHeader = () => {
  const t = localStorage.getItem("scaffoldx_token") || localStorage.getItem("DOSERNE_TOKEN");
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

export const inputCls =
  "w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-white/10 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-400";

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return iso; }
};

export function isoWeekNow() {
  const d = new Date();
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (dt.getUTCDay() + 6) % 7;
  dt.setUTCDate(dt.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(dt.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((dt - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${dt.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/** ISO week for a specific date (accepts YYYY-MM-DD). */
export function dateToIsoWeek(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (dt.getUTCDay() + 6) % 7;
  dt.setUTCDate(dt.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(dt.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((dt - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${dt.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/**
 * Small "W##" badge that appears next to a date input, computed live from
 * the ISO date value. Zero cost when value is empty.
 */
export function WeekBadge({ value, testId }) {
  const w = dateToIsoWeek(value);
  if (!w) return null;
  const num = w.split("-W")[1] || "";
  return (
    <span className="ml-1 inline-flex items-center text-[9px] uppercase tracking-widest font-black text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded whitespace-nowrap"
          data-testid={testId}
          title={`ISO week ${w}`}>
      W{num}
    </span>
  );
}

/** Business days (Mon-Fri) between two ISO dates, inclusive. */
export function businessDaysBetween(startStr, finishStr) {
  if (!startStr || !finishStr) return 0;
  const s = new Date(startStr), f = new Date(finishStr);
  if (isNaN(s) || isNaN(f) || f < s) return 0;
  let count = 0;
  const cur = new Date(s);
  while (cur <= f) {
    const day = cur.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/**
 * Return every ISO week the date range covers, together with how many business
 * days fall inside each week.
 * → [{ week: "2026-W32", days: 3 }, { week: "2026-W33", days: 5 }, ...]
 */
export function weeksInRange(startStr, finishStr) {
  if (!startStr || !finishStr) return [];
  const s = new Date(startStr), f = new Date(finishStr);
  if (isNaN(s) || isNaN(f) || f < s) return [];
  const byWeek = {};
  const cur = new Date(s);
  while (cur <= f) {
    const day = cur.getDay();
    if (day !== 0 && day !== 6) {
      const w = dateToIsoWeek(cur.toISOString().slice(0, 10));
      byWeek[w] = (byWeek[w] || 0) + 1;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return Object.entries(byWeek)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([week, days]) => ({ week, days }));
}

/** Field label wrapper */
export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <div className="mt-1 text-[10px] text-slate-500">{hint}</div>}
    </label>
  );
}

/** Status pill */
export function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.new_request;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-white/5 text-slate-300 border border-white/10">
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

/** Modal shell — handles Escape key + backdrop click */
export function ModalShell({ onClose, maxWidth = "max-w-2xl", children, testid = "modal" }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${maxWidth} max-h-[92vh] rounded-2xl border border-white/10 bg-[#0a1628] overflow-hidden flex flex-col`}
      >
        {children}
      </div>
    </div>
  );
}
